import { settingsActions, settingsStore } from '$lib/stores/settings'
// import { subscriptionService } from './SubscriptionService' - Removed: using UsageTrackingService instead

interface BareunApiConfig {
  baseUrl: string
  apiKey?: string
}

interface BareunApiRequest {
  text: string
  options?: {
    checkSpelling?: boolean
    checkGrammar?: boolean
    checkStyle?: boolean
  }
}

interface BareunApiError {
  errorCode: string
  message: string
  startPos: number
  endPos: number
  orgStr: string
  candWord: string[]
  help?: string
}

interface BareunRevisionBlock {
  origin: {
    content: string
    beginOffset: number
    length: number
  }
  revised: string
  revisions: Array<{
    revised: string
    comment?: string
  }>
}

interface BareunRevisedSentence {
  origin: string
  revised: string
  revisedBlocks?: BareunRevisionBlock[]
}

interface BareunApiResponse {
  origin: string
  revised: string
  revisedSentences?: BareunRevisedSentence[]
  result?: {
    errors: BareunApiError[]
    original: string
    corrected: string
  }
  status: 'success' | 'error'
  message?: string
}

/**
 * Client for a user-configured Bareun API server.
 * Default app traffic should go through appstore.junlim.org instead of direct Bareun access.
 * Handles Korean grammar checking API calls
 */
export class BareunApiClient {
  private config: BareunApiConfig
  
  constructor(config?: Partial<BareunApiConfig>) {
    // Use settings from store if not provided
    const apiUrl = config?.baseUrl || settingsActions.getApiUrl()
    const apiKey = config?.apiKey || settingsActions.getApiKey()
    this.config = {
      baseUrl: apiUrl,
      apiKey: apiKey || '' // No default API key - proxy handles authentication
    }
  }
  
  /**
   * Check text for grammar errors
   */
  async checkText(request: BareunApiRequest): Promise<BareunApiResponse> {
    // Note: Rate limiting is now handled by UsageTrackingService in GrammarCheckService
    
    // Use API key from config (proxy doesn't need it)
    const apiKey = this.config.apiKey || ''
    const url = `${this.config.baseUrl}/bareun/api/v1/correct-error`
    
    try {
      const requestBody = {
        document: {
          content: request.text,
          type: "PLAIN_TEXT"
        },
        encoding_type: "UTF8",
        auto_split: false
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': apiKey  // Changed from Authorization Bearer to api-key
        },
        body: JSON.stringify(requestBody)
      })
      
      if (!response.ok) {
        return this.getMockResponse(request.text)
      }
      
      const data = await response.json()
      
      // Parse the response and convert to our format
      const parsedResponse = this.parseApiResponse(data, request.text)
      
      // Note: Usage tracking is handled by UsageTrackingService in GrammarCheckService
      
      return parsedResponse
    } catch (error) {
      return this.getMockResponse(request.text)
    }
  }
  
  /**
   * Test API connection
   */
  async testConnection(): Promise<boolean> {
    const url = `${this.config.baseUrl}/health`
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.config.apiKey ? { 'Authorization': `Bearer ${this.config.apiKey}` } : {}
      })
      
      return response.ok
    } catch {
      return false
    }
  }
  
  /**
   * Update configuration
   */
  updateConfig(config: Partial<BareunApiConfig>) {
    this.config = {
      ...this.config,
      ...config
    }
  }
  
  /**
   * Update configuration from settings store
   */
  updateFromSettings() {
    this.config = {
      baseUrl: settingsActions.getApiUrl(),
      apiKey: settingsActions.getApiKey() || '' // No default API key - proxy handles authentication
    }
  }
  
  /**
   * Parse API response from bareun format to our format
   */
  private parseApiResponse(data: any, originalText: string): BareunApiResponse {
    const errors: BareunApiError[] = []
    const correctionMap = new Map<string, BareunApiError>()
    
    // Check if no errors found
    let hasErrors = false
    if (data.revisedSentences && Array.isArray(data.revisedSentences)) {
      for (const sentence of data.revisedSentences) {
        if (sentence.revisedBlocks && sentence.revisedBlocks.length > 0) {
          hasErrors = true
          break
        }
      }
    }
    
    if (!hasErrors) {
      return {
        origin: data.origin || originalText,
        revised: data.revised || originalText,
        result: {
          errors: [],
          original: originalText,
          corrected: data.revised || originalText
        },
        status: 'success'
      }
    }
    
    // Parse revised sentences and blocks
    if (data.revisedSentences && Array.isArray(data.revisedSentences)) {
      data.revisedSentences.forEach((sentence: BareunRevisedSentence, idx: number) => {
        
        if (sentence.revisedBlocks && Array.isArray(sentence.revisedBlocks)) {
          sentence.revisedBlocks.forEach((block: BareunRevisionBlock, blockIdx: number) => {
            // Get the original text from block.origin.content
            const original = block.origin.content
            const blockStart = block.origin.beginOffset
            // Use character length, not byte length
            const blockEnd = blockStart + original.length
            
            if (original.trim() && block.revised !== original) {
              // Get help text from comments
              let helpText = ''
              const suggestions = [block.revised]
              
              if (block.revisions && Array.isArray(block.revisions)) {
                const firstRevisionWithComment = block.revisions.find(rev => rev.comment)
                if (firstRevisionWithComment?.comment) {
                  helpText = firstRevisionWithComment.comment
                }
                
                // Add additional suggestions
                block.revisions.forEach(revision => {
                  if (revision.revised && revision.revised !== block.revised && !suggestions.includes(revision.revised)) {
                    suggestions.push(revision.revised)
                  }
                })
              }
              
              // Create unique key for deduplication
              const correctionKey = `${original.trim()}-${suggestions.join(',')}`
              
              if (!correctionMap.has(correctionKey)) {
                const error: BareunApiError = {
                  errorCode: this.determineErrorType(original, block.revised),
                  message: helpText || `'${original}'을(를) '${block.revised}'(으)로 교정하는 것을 권장합니다.`,
                  startPos: blockStart,
                  endPos: blockEnd,
                  orgStr: original,
                  candWord: suggestions,
                  help: helpText
                }
                
                correctionMap.set(correctionKey, error)
              }
            }
          })
        }
      })
    }
    
    // Convert map to array
    errors.push(...correctionMap.values())
    
    return {
      origin: data.origin || originalText,
      revised: data.revised || originalText,
      revisedSentences: data.revisedSentences,
      result: {
        errors,
        original: originalText,
        corrected: data.revised || originalText
      },
      status: 'success'
    }
  }
  
  /**
   * Determine error type based on the change
   */
  private determineErrorType(original: string, corrected: string): string {
    // Remove spaces for comparison
    const origNoSpace = original.replace(/\s/g, '')
    const corrNoSpace = corrected.replace(/\s/g, '')
    
    if (origNoSpace === corrNoSpace) {
      return 'SPACING_ERROR'
    } else if (original.length !== corrected.length) {
      return 'SPELLING_ERROR'
    } else {
      return 'GRAMMAR_ERROR'
    }
  }
  
  /**
   * Get mock response for development
   */
  private getMockResponse(text: string): BareunApiResponse {
    const revisedSentences: BareunRevisedSentence[] = []
    let revised = text
    
    // Create a revised sentence structure similar to the real API
    const sentence: BareunRevisedSentence = {
      origin: text,
      revised: text,
      revisedBlocks: []
    }
    
    // Mock error detection - 더 다양한 오류 패턴
    if (text.includes('않 습니다')) {
      const pos = text.indexOf('않 습니다')
      sentence.revisedBlocks?.push({
        origin: {
          content: '않 습니다',
          beginOffset: pos,
          length: 6
        },
        revised: '않습니다',
        revisions: [{
          revised: '않습니다',
          comment: '보조 용언은 앞말에 붙여 씁니다.'
        }]
      })
      revised = revised.replace('않 습니다', '않습니다')
    }
    
    if (text.includes('되요')) {
      const pos = text.indexOf('되요')
      sentence.revisedBlocks?.push({
        origin: {
          content: '되요',
          beginOffset: pos,
          length: 2
        },
        revised: '돼요',
        revisions: [
          {
            revised: '돼요',
            comment: '"되다"의 활용형은 "돼요"가 올바른 표현입니다.'
          },
          {
            revised: '되어요',
            comment: '"되다"의 활용형은 "되어요"로도 쓸 수 있습니다.'
          }
        ]
      })
      revised = revised.replace('되요', '돼요')
    }
    
    if (text.includes('그 런데')) {
      const pos = text.indexOf('그 런데')
      sentence.revisedBlocks?.push({
        origin: {
          content: '그 런데',
          beginOffset: pos,
          length: 4
        },
        revised: '그런데',
        revisions: [{
          revised: '그런데',
          comment: '한 단어는 붙여 씁니다.'
        }]
      })
      revised = revised.replace('그 런데', '그런데')
    }
    
    if (text.includes('했 습니다')) {
      const pos = text.indexOf('했 습니다')
      sentence.revisedBlocks?.push({
        origin: {
          content: '했 습니다',
          beginOffset: pos,
          length: 5
        },
        revised: '했습니다',
        revisions: [{
          revised: '했습니다',
          comment: '보조 용언은 앞말에 붙여 씁니다.'
        }]
      })
      revised = revised.replace('했 습니다', '했습니다')
    }
    
    // Update sentence revised text
    sentence.revised = revised
    
    // Only add sentence if there are blocks
    if (sentence.revisedBlocks && sentence.revisedBlocks.length > 0) {
      revisedSentences.push(sentence)
    }
    
    // Now parse this mock response through the same parser
    return this.parseApiResponse({
      origin: text,
      revised: revised,
      revisedSentences: revisedSentences
    }, text)
  }
}

// Singleton instance
export const bareunApiClient = new BareunApiClient()
